import { createContext, forwardRef, useContext, useId } from "react";
import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  accordionSummaryClasses,
  type AccordionProps as MuiAccordionProps,
} from "@mui/material";

import { Icon } from "../Icon";
import { Typography, type TypographyColor } from "../Typography";
import { tokens } from "../../tokens/generated/tokens";

import {
  AccordionGroupContext,
  type AccordionVariant,
} from "./AccordionGroupContext";

import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

export type { AccordionVariant };

/** Element wrapping the disclosure button: a heading at the level the page outline needs, or no heading at all. */
export type AccordionHeadingComponent =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "p"
  | "div";

/** Either color scheme's token set. The union makes every read valid in both. */
type SchemeTokens = (typeof tokens.theme)[keyof typeof tokens.theme];

/** Same scoping as `Alert`: dark values apply unless a nearer scope pins light. */
const DARK_SCHEME =
  '[data-mui-color-scheme="dark"] &:not([data-mui-color-scheme="light"] *)';

const size = tokens.base.size;
const { borderRadius, borderWidth } = tokens.functional.size;
const toggleControl = tokens.pattern.size.control.small;

const px = (value: number): string => `${value}px`;

/**
 * Whether a slot renders anything. React renders nothing for `null`,
 * `undefined` and booleans, so `action={isReady && <Link />}` is empty when
 * false, while `meta={0}` still renders.
 */
const isRendered = (node: ReactNode): boolean =>
  node !== null && node !== undefined && typeof node !== "boolean";

type SxArray = Extract<SxProps<Theme>, readonly unknown[]>;

const isSxArray = (sx: SxProps<Theme>): sx is SxArray => Array.isArray(sx);

/** MUI's `sx` array form takes no nested arrays, so a consumer's array is spread. */
const toSxArray = (sx: SxProps<Theme> | undefined): SxArray => {
  if (sx === undefined) return [];
  return isSxArray(sx) ? sx : [sx];
};

export interface AccordionProps extends Omit<
  MuiAccordionProps,
  "variant" | "square" | "children" | "slots" | "slotProps" | "title"
> {
  /** Names the disclosure button, and the heading when `headingComponent` is one. */
  title: ReactNode;
  /** Title color, bound to a theme token. Default: "textPrimary". */
  titleColor?: TypographyColor;
  /**
   * Chips, tags or counts shown beside the title inside the disclosure button.
   * Announced as the button's description, not its name. Phrasing content
   * only, since it renders inside a `<button>`: render a `Chip` with
   * `component="span"`.
   */
  meta?: ReactNode;
  /**
   * Wraps the disclosure button. Required, with no default: no single heading
   * level fits every page outline (WCAG 1.3.1), so each call site picks the
   * level its outline needs, or `p` / `div` when the header is not a heading.
   */
  headingComponent: AccordionHeadingComponent;
  /**
   * Header action, such as a link or button. Renders beside the disclosure
   * button, never inside it, so it stays reachable and keeps out of the
   * button's accessible name.
   */
  action?: ReactNode;
  /** Details revealed on expand. */
  children: ReactNode;
  /** Surface treatment. Default: "contained". Inside an `AccordionGroup`, the group's variant wins. */
  variant?: AccordionVariant;
  /** Hands the container treatment to a group wrapper. Default: false. `AccordionGroup` sets it. */
  grouped?: boolean;
  /** Identifies this accordion in its `AccordionGroup`'s `expanded`, `defaultExpanded` and `onChange`. */
  value?: string;
  /** No block-end gutter when true. Default: true. */
  disableGutters?: boolean;
  /** Paper elevation. Default: 0; neither variant carries a shadow. */
  elevation?: number;
  /** MUI slot props, minus `heading`, which this component owns. */
  slotProps?: Omit<NonNullable<MuiAccordionProps["slotProps"]>, "heading">;
  /** Root test id. Default: "ds-accordion". */
  "data-testid"?: string;
}

interface Header {
  headingComponent: AccordionHeadingComponent;
  action: ReactNode;
  dataTestId: string;
}

/**
 * Carries the heading element and `action` to the heading slot. MUI types the
 * slot's props as a plain `<h3>`, and a slot component defined per render
 * would remount the disclosure button and drop its focus, so context is the
 * route that keeps both intact.
 */
const HeaderContext = createContext<Header | null>(null);

interface HeadingProps extends ComponentPropsWithoutRef<"div"> {
  /** Passed by MUI to non-host slot components; not a DOM attribute. */
  ownerState?: unknown;
}

/**
 * Replaces MUI's heading slot, a fixed `<h3>`, so the header row holds two
 * siblings: the chosen heading element wrapping the disclosure button, and the
 * action. A heading can't go inside the button, which only takes phrasing
 * content, so the wrapper is where the level is set.
 */
const HeaderRow = forwardRef<HTMLDivElement, HeadingProps>(
  ({ ownerState: _ownerState, ...headingProps }, ref) => {
    const header = useContext(HeaderContext);
    if (!header) {
      throw new Error("AccordionHeaderRow renders only inside Accordion");
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: px(size[8]) }}>
        <Box
          component={header.headingComponent}
          ref={ref}
          sx={{ all: "unset", flex: 1, minWidth: 0 }}
          {...headingProps}
        />
        {isRendered(header.action) && (
          <Box
            data-testid={`${header.dataTestId}-action`}
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              paddingInlineEnd: px(size[16]),
            }}
          >
            {header.action}
          </Box>
        )}
      </Box>
    );
  },
);

HeaderRow.displayName = "AccordionHeaderRow";

const buildSurfaceSx = (
  { bgColor, borderColor, button, focus }: SchemeTokens,
  variant: AccordionVariant,
  isGrouped: boolean,
): Record<string, unknown> => {
  const border = `${px(borderWidth.default)} solid ${borderColor.default}`;
  const surfaceByVariant: Record<AccordionVariant, Record<string, unknown>> = {
    plain: { backgroundColor: "transparent", border: "none" },
    contained: isGrouped
      ? {
          backgroundColor: bgColor.default,
          border: "none",
          borderBlockEnd: border,
          "&:last-child": { borderBlockEnd: "none" },
        }
      : { backgroundColor: bgColor.default, border },
  };

  return {
    ...surfaceByVariant[variant],
    [`& .${accordionSummaryClasses.expandIconWrapper} > span`]: {
      color: button.neutral.iconColor.rest,
    },
    [`& .${accordionSummaryClasses.root}:hover .${accordionSummaryClasses.expandIconWrapper} > span`]:
      { backgroundColor: button.neutral.bgColor.hover },
    [`& .${accordionSummaryClasses.focusVisible}`]: {
      outline: `${px(borderWidth.thick)} solid ${focus.outlineColor}`,
    },
    "& .MuiAccordionDetails-root": {
      borderBlockStart: variant === "contained" ? border : "none",
    },
  };
};

const buildRootSx = (
  variant: AccordionVariant,
  isGrouped: boolean,
  hasGutter: boolean,
): SystemStyleObject<Theme> => ({
  boxShadow: "none",
  borderRadius:
    variant === "contained" && !isGrouped ? px(borderRadius.default) : 0,
  marginBlockEnd: hasGutter ? px(size[8]) : 0,
  "&::before": { display: "none" },
  ...buildSurfaceSx(tokens.theme.light, variant, isGrouped),
  [DARK_SCHEME]: buildSurfaceSx(tokens.theme.dark, variant, isGrouped),
});

/**
 * Leading chevron, an `IconButton`'s look without its element: a real button
 * here would nest inside the summary button.
 */
const toggleSx: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: px(toggleControl.size),
  height: px(toggleControl.size),
  padding: px(toggleControl.paddingBlock),
  borderRadius: px(borderRadius.full),
  boxSizing: "border-box",
};

const buildSummarySx = (hasAction: boolean): SxProps<Theme> => ({
  flexDirection: "row-reverse",
  gap: px(size[8]),
  minHeight: 0,
  paddingBlock: px(size[12]),
  paddingInlineStart: px(size[16]),
  paddingInlineEnd: hasAction ? 0 : px(size[16]),
  fontFamily: "inherit",
  [`& .${accordionSummaryClasses.content}`]: {
    margin: 0,
    minWidth: 0,
    alignItems: "center",
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    transition: "transform 150ms",
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    { transform: "rotate(90deg)" },
  [`&.${accordionSummaryClasses.focusVisible}`]: {
    backgroundColor: "transparent",
    outlineOffset: px(-borderWidth.thick),
  },
});

const detailsSx: SxProps<Theme> = { padding: px(size[16]) };

const headerContentSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: px(size[8]),
};

/** Centro/Kaizen disclosure panel over MUI's Accordion, with a header action slot outside the disclosure button. */
export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (props, ref) => {
    const {
      title,
      titleColor = "textPrimary",
      meta,
      headingComponent,
      action,
      children,
      variant = "contained",
      grouped = false,
      value,
      disableGutters = true,
      elevation = 0,
      expanded,
      onChange,
      slotProps,
      sx,
      "data-testid": dataTestId = "ds-accordion",
      ...rest
    } = props;

    const group = useContext(AccordionGroupContext);
    const fallbackValue = useId();
    const summaryId = useId();
    const titleId = useId();
    const metaId = useId();
    const detailsId = useId();

    const groupValue = value ?? fallbackValue;
    const resolvedVariant = group?.variant ?? variant;
    const isGrouped = group !== null || grouped;
    const hasAction = isRendered(action);
    const hasMeta = isRendered(meta);

    const resolvedExpanded = group ? group.isExpanded(groupValue) : expanded;
    const handleChange: MuiAccordionProps["onChange"] = (event, isExpanded) => {
      group?.toggle(event, groupValue, isExpanded);
      onChange?.(event, isExpanded);
    };

    return (
      <HeaderContext.Provider value={{ headingComponent, action, dataTestId }}>
        <MuiAccordion
          ref={ref}
          data-testid={dataTestId}
          elevation={elevation}
          square
          disableGutters
          expanded={resolvedExpanded}
          onChange={handleChange}
          slots={{ heading: HeaderRow }}
          slotProps={slotProps}
          sx={[
            buildRootSx(resolvedVariant, isGrouped, !disableGutters),
            ...toSxArray(sx),
          ]}
          {...rest}
        >
          <AccordionSummary
            id={summaryId}
            aria-controls={detailsId}
            aria-labelledby={titleId}
            aria-describedby={hasMeta ? metaId : undefined}
            data-testid={`${dataTestId}-summary`}
            expandIcon={
              <Box component="span" sx={toggleSx}>
                <Icon name="chevronRight" size="sm" />
              </Box>
            }
            sx={buildSummarySx(hasAction)}
          >
            <Box component="span" sx={headerContentSx}>
              <Typography
                id={titleId}
                component="span"
                variant="body-medium"
                color={titleColor}
                data-testid={`${dataTestId}-title`}
              >
                {title}
              </Typography>
              {hasMeta && (
                <Box component="span" id={metaId} sx={headerContentSx}>
                  {meta}
                </Box>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            data-testid={`${dataTestId}-details`}
            sx={detailsSx}
          >
            {children}
          </AccordionDetails>
        </MuiAccordion>
      </HeaderContext.Provider>
    );
  },
);

Accordion.displayName = "Accordion";
